package net.dipgo.game.demo

import net.dipgo.game.*
import net.dipgo.game.demo.PositionIndexToCharacterConverter.Companion.characterToBoardIndex
import net.dipgo.game.board.BoardPosition
import net.dipgo.game.board.ZobristHash
import net.dipgo.gamerooms.DemoBoardSituation


sealed class DemoBoardMove
object DemoResign : DemoBoardMove()
object DemoPass : DemoBoardMove()

/**
 * A skipped turn (encoded `"02"`) — a forgiven timeout on the server, where the
 * timed-out player misses a turn instead of resigning. It advances the rotation
 * and places no stone (no capture/score effect, no ko/suicide bookkeeping). For
 * the end-of-play check it counts as a pass: the turn was given up, so a round
 * of passes and skips ends play exactly as a round of passes does, matching the
 * server.
 */
object DemoSkip : DemoBoardMove()
data class DemoMove(val xCoord: Int, val yCoord: Int) : DemoBoardMove()

class DemoDomain {
    fun movestringToSituation(
        boardSize: Int,
        numberOfTeams: Int,
        movesAsString: String?,
        komiMap: Map<Int, Double> = mapOf(),
        pointsForCaptures: Boolean = true,
        removeStonesOnResign: Boolean = false,
        atMove: Int? = null
    ): DemoBoardSituation {
        require(numberOfTeams >= 2)

        var moveCounter: Int = 0
        val allTeams = PointState.nonEmptyPointStates().take(numberOfTeams)
        val remainingTeams: MutableList<PointState> = allTeams
            .take(numberOfTeams).toMutableList()
        var teamToMove: PointState = allTeams.first()

        val board = BoardPosition(boardSize, boardSize)
        //this is converted to a list of moves independent of who made them, this is then deduced
        val movestringAtGivenMove = if (atMove != null && movesAsString != null) {
            // Use Long to avoid Int overflow for large atMove values (e.g. atMove=1073741824 → atMove*2 wraps negative).
            val charsToTake = (atMove.toLong() * 2).coerceIn(0L, movesAsString.length.toLong()).toInt()
            movesAsString.take(charsToTake)
        } else movesAsString

        val moves = decipherMoveString(movestringAtGivenMove)
        val gameLogic = GameLogicImp(board)

        val scores: MutableMap<Int, TeamScore> = if (komiMap.isEmpty()) {
            allTeams.associate { it.pointStateToPointStateInt() to TeamScore(0, 0.0, 0) }
        } else {
            allTeams.associate { team ->
                team.pointStateToPointStateInt() to TeamScore(
                    0,
                    komiMap[team.pointStateToPointStateInt()] ?: 0.0,
                    0
                )
            }
        }.toMutableMap()

        val positionHashes = mutableSetOf(ZobristHash.hash(board))

        var continueProcessing = true
        var truncatedMovestring = movesAsString
        moves.forEachIndexed { i, move ->
            if (continueProcessing) {
                val initialStonesOnBoard = board.numberOfStonesOnBoard()
                val initialTeamToMove = teamToMove
                if (move is DemoMove) {
                    gameLogic.makeMove(teamToMove, Point(move.xCoord, move.yCoord))

                    if (board.getStateOfPoint(move.xCoord, move.yCoord) == PointState.EMPTY) {
                        //suicide rule violated — the attempted stone was reverted and
                        //captured nothing, so the live state already equals the
                        //situation just before this move. Finalize directly instead
                        //of re-replaying the prefix.
                        val movestringAtThisMove = movesAsString?.take(i * 2) ?: ""
                        return DemoBoardSituation(
                            boardPosition = board,
                            playerToMove = teamToMove.pointStateToPointStateInt(),
                            scores = finalizeScores(board, scores, gameLogic),
                            remainingTeams = remainingTeams,
                            lastMove = if (i == 0) null else moves[i - 1],
                            movestring = movestringAtThisMove,
                            moveResult = MoveResult.FAILURE_SUICIDE,
                            captures = pointsForCaptures
                        )
                    }
                }

                val currentHash = ZobristHash.hash(board)
                if (positionHashes.contains(currentHash) && move is DemoMove) {
                    //ko rule broken — unlike suicide, a ko revert is not proven side-effect-free
                    //(the move did capture), so the pre-move board is reconstructed by replaying
                    //the legal prefix. Replay intentionally retained here; see the hardening plan.
                    val amountOfMovestringToTake = ((i * 2))

                    val movestringAtThisMove = movesAsString?.take(amountOfMovestringToTake) ?: ""

                    val currentSituation = movestringToSituation(
                        boardSize,
                        numberOfTeams,
                        movestringAtThisMove,
                        komiMap,
                        pointsForCaptures,
                        removeStonesOnResign,
                        atMove
                    )
                    return DemoBoardSituation(
                        boardPosition = currentSituation.boardPosition,
                        playerToMove = currentSituation.playerToMove,
                        scores = currentSituation.scores,
                        remainingTeams = currentSituation.remainingTeams,
                        lastMove = currentSituation.lastMove,
                        movestring = movestringAtThisMove,
                        moveResult = MoveResult.FAILURE_KO,
                        captures = pointsForCaptures,
                    )
                } else {
                    positionHashes.add(currentHash)
                }

                moveCounter += 1

                val originalTeamToMove = teamToMove
                teamToMove = if (remainingTeams.indexOf(teamToMove) == remainingTeams.size - 1) {
                    remainingTeams.first()
                } else {
                    remainingTeams[remainingTeams.indexOf(teamToMove) + 1]
                }
                if (move is DemoResign) {
                    remainingTeams.remove(originalTeamToMove)
                    if (removeStonesOnResign && remainingTeams.size >= 2) {
                        board.removeAllStonesOfColour(originalTeamToMove)
                    }
                    if (remainingTeams.size == 1) {
                        continueProcessing = false
                        truncatedMovestring = movesAsString?.take(moveCounter * 2)
                        teamToMove = PointState.EMPTY
                    }
                } else if (move is DemoPass || move is DemoSkip) {
                    val movesToConsider = moves.take(i + 1).filterIndexed { j, thisMove -> j > i - remainingTeams.size }
                    // A round of given-up turns (passes and skipped turns alike) no longer
                    // hard-ends the game: on the server it pauses play for the dead-stone
                    // scoring phase, from which any team may resume. Moves recorded after the
                    // round are resumed play and must be replayed, so the game is only over
                    // when the round ends the recorded movestring itself (not merely the
                    // atMove-truncated prefix — a mid-record pause keeps the rotation alive so
                    // navigation and branch-off URLs stay intact).
                    val endsTheRecord = (i + 1) * 2 == (movesAsString?.length ?: 0)
                    if (movesToConsider.size == remainingTeams.size
                        && movesToConsider.all { it is DemoPass || it is DemoSkip }
                        && endsTheRecord
                    ) {
                        continueProcessing = false
                        truncatedMovestring = movesAsString?.take(moveCounter * 2)
                        teamToMove = PointState.EMPTY
                    }
                }

                if (pointsForCaptures && move is DemoMove) {
                    val currentStonesOnBoard = board.numberOfStonesOnBoard()
                    if (initialStonesOnBoard + 1 != currentStonesOnBoard) {
                        val pointsToAward = initialStonesOnBoard - (currentStonesOnBoard) + 1
                        val teamAsInt = initialTeamToMove.pointStateToPointStateInt()
                        val currentScore: TeamScore = scores[teamAsInt]!!
                        scores[teamAsInt] =
                            TeamScore(currentScore.points, currentScore.komi, currentScore.captures + pointsToAward)
                    }
                }
            }
        }
        val finalScore = finalizeScores(board, scores, gameLogic)

        return DemoBoardSituation(
            board,
            teamToMove.pointStateToPointStateInt(),
            finalScore,
            remainingTeams,
            moves.lastOrNull(),
            movestring = truncatedMovestring ?: "",
            captures = pointsForCaptures,
        )

    }

    private fun finalizeScores(
        board: BoardPosition,
        scores: Map<Int, TeamScore>,
        gameLogic: GameLogicImp,
    ): Map<Int, TeamScore> {
        val scoringLogic = ScoringLogicImp(gameLogic)
        val positionScore: Map<Int, Int> = scoringLogic.pointsForThisBoardPositionExcludingCapturesAndKomi(board)
        return scores.map {
            it.key to TeamScore(
                it.value.points + (positionScore[it.key] ?: 0),
                it.value.komi,
                it.value.captures
            )
        }.toMap()
    }

    private fun decipherMoveString(movesAsString: String?): List<DemoBoardMove> {
        if (movesAsString == null) {
            return emptyList()
        }

        val positionToCharacter = (movesAsString).mapIndexed { i, c -> i to c }.toMap()

        val moveList = mutableListOf<DemoBoardMove>()

        for (i in movesAsString.indices step 2) {
            if (positionToCharacter[i]!! == '0') {
                val secondChar = positionToCharacter[i + 1]!!
                if (secondChar == '0') {
                    moveList.add(
                        DemoResign
                    )
                } else if (secondChar == '2') {
                    moveList.add(
                        DemoSkip
                    )
                } else {
                    moveList.add(
                        DemoPass
                    )
                }
            } else {
                moveList.add(
                    DemoMove(
                        characterToBoardIndex(positionToCharacter[i]!!),
                        characterToBoardIndex(positionToCharacter[i + 1]!!)
                    )
                )
            }
        }

        return moveList
    }

}

class PositionIndexToCharacterConverter {
    companion object {
        fun characterToBoardIndex(char: Char): Int {
            return when (char) {
                'a' -> 0
                'b' -> 1
                'c' -> 2
                'd' -> 3
                'e' -> 4
                'f' -> 5
                'g' -> 6
                'h' -> 7
                'j' -> 8
                'k' -> 9
                'l' -> 10
                'm' -> 11
                'n' -> 12
                'o' -> 13
                'p' -> 14
                'q' -> 15
                'r' -> 16
                's' -> 17
                't' -> 18
                'u' -> 19
                'v' -> 20
                'w' -> 21
                'x' -> 22
                'y' -> 23
                'z' -> 24
                else -> throw RuntimeException("Unsupported Character \'${char}\'")
            }
        }

        fun boardIndexToCharacter(index: Int): Char {
            return when (index) {
                0 -> 'a'
                1 -> 'b'
                2 -> 'c'
                3 -> 'd'
                4 -> 'e'
                5 -> 'f'
                6 -> 'g'
                7 -> 'h'
                8 -> 'j'
                9 -> 'k'
                10 -> 'l'
                11 -> 'm'
                12 -> 'n'
                13 -> 'o'
                14 -> 'p'
                15 -> 'q'
                16 -> 'r'
                17 -> 's'
                18 -> 't'
                19 -> 'u'
                20 -> 'v'
                21 -> 'w'
                22 -> 'x'
                23 -> 'y'
                24 -> 'z'
                else -> throw RuntimeException("Unsupported Board Index \'${index}\'")
            }
        }
    }

}

